/**
 * CounterNode.tsx
 * Counterノードのビューコンポーネント
 * 初期値・増分・最終値を設定して、反復実行に使うノード
 */
import { memo } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import type { CounterNode } from '../../types';
import { COUNTER_SOURCE_HANDLES, COUNTER_TARGET_HANDLES } from '../counterHandles';
import { getNodeDisplayLabel, useFlowDisplayLanguage } from '../../displayLanguage';
import { CardShell } from '../CardShell';

/**
 * Counterノードの表示コンポーネント
 * - 3つの数値設定を持つ
 * - 左右に接続ハンドルを持つ
 */
export const CounterNodeView = memo(function CounterNodeView({
  data,
}: NodeProps<CounterNode>) {
  const displayLanguage = useFlowDisplayLanguage();
  const summary = `${data.initialValue} → ${data.finalValue}（${data.stepValue >= 0 ? '+' : ''}${data.stepValue}）`;

  return (
    <CardShell
      kind="counter"
      accent="amber"
      title={getNodeDisplayLabel('counter', displayLanguage)}
      summary={summary}
    >
      <div className="flow-node__port-label flow-node__port-label--in text-amber-700" style={{ top: '30%', transform: 'translateY(-50%)' }}>
        {displayLanguage === 'en' ? 'from start' : 'はじめから'}
      </div>
      <div className="flow-node__port-label flow-node__port-label--in text-amber-700" style={{ top: '72%', transform: 'translateY(-50%)' }}>
        {displayLanguage === 'en' ? 'loop back' : 'もどる'}
      </div>
      <div className="flow-node__port-label flow-node__port-label--out text-amber-700" style={{ top: '30%', transform: 'translateY(-50%)' }}>
        {displayLanguage === 'en' ? 'repeat' : 'くりかえす'}
      </div>
      <div className="flow-node__port-label flow-node__port-label--out text-amber-700" style={{ top: '72%', transform: 'translateY(-50%)' }}>
        {displayLanguage === 'en' ? 'done' : 'おわり'}
      </div>

      <Handle
        id={COUNTER_TARGET_HANDLES.initial}
        type="target"
        position={Position.Left}
        style={{ top: '30%' }}
        className="!bg-amber-500"
      />
      <Handle
        id={COUNTER_TARGET_HANDLES.repeat}
        type="target"
        position={Position.Left}
        style={{ top: '72%' }}
        className="!bg-amber-500"
      />
      <Handle
        id={COUNTER_SOURCE_HANDLES.inRange}
        type="source"
        position={Position.Right}
        style={{ top: '30%' }}
        className="!bg-amber-500"
      />
      <Handle
        id={COUNTER_SOURCE_HANDLES.done}
        type="source"
        position={Position.Right}
        style={{ top: '72%' }}
        className="!bg-amber-500"
      />
    </CardShell>
  );
});
